import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { AppHeader } from "./components/AppHeader";
import { ChatFooter } from "./components/ChatFooter";
import { CookieConsent } from "./components/CookieConsent";
import { captureAttribution, initMetaPixel, trackPixel } from "./lib/marketing";
import { trackPageView } from '@/utils/analytics';
import { WelcomeModal, useWelcomeModal } from "./components/auth/WelcomeModal";
import { useAuthContext } from "./contexts/AuthContext";
import { useSubscription } from "./hooks/useSubscription";
import Index from "./pages/Index";
import TicketRewardsLanding from "./pages/TicketRewardsLanding";
import VideoRotator from "./pages/VideoRotator";
import VideoCompressor from "./pages/VideoCompressor";
import VideoStitcher from "./pages/VideoStitcher";
import VideoSplitter from "./pages/VideoSplitter";
import VideoConcatenate from "./pages/VideoConcatenate";
import VideoTranscriber from "./pages/VideoTranscriber";
import VideoEnhancerNLE from "./pages/VideoEnhancerNLE";
import VideoEnhancerPage from "./pages/VideoEnhancerPage";
import AudioEnhancer from "./pages/AudioEnhancer";
import BackgroundRemover from "./pages/BackgroundRemover";
import SceneEdit from "./pages/SceneEdit";
import NameSlate from "./pages/NameSlate";
import Captions from "./pages/Captions";
import AdminDashboard from "./pages/AdminDashboard";
import ScheduledPosts from "./pages/ScheduledPosts";
import AccountPage from "./pages/AccountPage";
import BillingPage from "./pages/BillingPage";
import PlansPage from "./pages/PlansPage";
import AuditionEnhancer from "./pages/SelfTapeEnhancer";
import ReadersPage from "./pages/ReadersPage";
import AuthPage from "./pages/AuthPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TrialPage from "./pages/TrialPage";
import SubscribeReaderPage from "./pages/SubscribeReaderPage";
import ReaderProfilePage from "./pages/ReaderProfilePage";
import ReaderSessionsPage from "./pages/ReaderSessionsPage";
import ReaderProfileEditPage from "./pages/ReaderProfileEditPage";
import CharterPage from "./pages/CharterPage";
import PromoPopup from "./components/PromoPopup";
import OfferPage from "./pages/OfferPage";
import DropPage from "./pages/DropPage";
import PartnerOfferPage from "./pages/PartnerOfferPage";
import PartnerHandoffPage from "./pages/PartnerHandoffPage";
import ReaderPayoutsPage from "./pages/ReaderPayoutsPage";
import AffiliatePage from "./pages/AffiliatePage";
import CreatorStorePage from "./pages/CreatorStorePage";
import CoursePage from "./pages/CoursePage";
import MyStorePage from "./pages/MyStorePage";
import CreatorGuidePage from "./pages/CreatorGuidePage";
import MyPurchasesPage from "./pages/MyPurchasesPage";
import MySitePage from "./pages/MySitePage";
import SiteReviewPage from "./pages/SiteReviewPage";
import SitesAdminPage from "./pages/SitesAdminPage";
import MoveYourSitePage from "./pages/MoveYourSitePage";
import ScanPage from './pages/ScanPage';
import ProductViewerPage from "./pages/ProductViewerPage";
import MarketplacePage from "./pages/MarketplacePage";
import DiscoverPage from "./pages/DiscoverPage";
import WebinarCalendarPage from "./pages/WebinarCalendarPage";
import HoustonPage from "./pages/HoustonPage";
import HoustonProducersPage from "./pages/HoustonProducersPage";
import FindTalentPage from "./pages/FindTalentPage";
import MySubscriptionsPage from "./pages/MySubscriptionsPage";
import CourseBuilderPage from "./pages/CourseBuilderPage";
import ListingChooserPage from "./pages/ListingChooserPage";
import BundleBuilderPage from "./pages/BundleBuilderPage";
import GoodsBuilderPage from "./pages/GoodsBuilderPage";
import WebinarBuilderPage from "./pages/WebinarBuilderPage";
import VideoBuilderPage from "./pages/VideoBuilderPage";
import SeriesStudioPage from "./pages/SeriesStudioPage";
import ImageStudioPage from "./pages/ImageStudioPage";
import InterviewHostPage from "./pages/InterviewHostPage";
import MyLibraryPage from "./pages/MyLibraryPage";
import InterviewGuestPage from "./pages/InterviewGuestPage";
import GameShowsPage from "./pages/GameShowsPage";
import GameShowHostPage from "./pages/GameShowHostPage";
import GameShowAudiencePage from "./pages/GameShowAudiencePage";
import GameShowContestantPage from "./pages/GameShowContestantPage";
import GameShowSubmitPage from "./pages/GameShowSubmitPage";
import SponsorPayPage from "./pages/SponsorPayPage";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '720144901245-bk9e3a15c0tfnui7jtbjpqqrp1032chv.apps.googleusercontent.com';

const STANDALONE_PATHS = ['/auth', '/reset-password', '/subscribe/reader'];
// Named event landings. These are printed on flyers and pasted into 141 cold
// outreach emails, so the URL is the event's name rather than an id, and the page
// carries no marketplace chrome: someone arriving from their hockey club's
// newsletter has no use for a "Marketplace / Joe B /" breadcrumb or a site nav.
// The path segment is looked up as a product vanityCode server-side.
const NAMED_LANDINGS = ['/john-wroblewski'];

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthContext();
  const location = useLocation();

  if (isLoading) return null;

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location.pathname + location.search }} replace />;
  }

  return <>{children}</>;
}

// React Router carries the previous page's scroll position into the next route,
// so clicking a card from halfway down the marketplace landed you halfway down
// the product page — on a shorter page, at the bottom of it. Reset on forward
// navigation only: POP is a back/forward press, where returning to where you
// were is the right behavior, and a #hash target owns its own scroll.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  React.useEffect(() => {
    if (navigationType === 'POP' || hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash, navigationType]);

  return null;
}

function AppLayout() {
  const location = useLocation();
  // An alternate-audience landing (/course/:id?v=hockey) is pointed at by ads and
  // read by people who have never heard of us. The actor chrome — Tools, Readers,
  // Marketplace, Hey Joe Casting — tells a hockey parent they're in the wrong
  // place before they reach the copy, so the alt views run without it.
  const isAltLanding = location.pathname.startsWith('/course/') && new URLSearchParams(location.search).has('v');
  const isStandalone = isAltLanding || NAMED_LANDINGS.includes(location.pathname) || STANDALONE_PATHS.includes(location.pathname) || location.pathname.startsWith('/interview/') || location.pathname.startsWith('/my-store/interviews/') || location.pathname.startsWith('/session/');
  const { isAuthenticated } = useAuthContext();
  const { isSubscriber, loading: subLoading } = useSubscription();

  // Ad attribution: capture the landing campaign once, then fire a Meta PageView
  // per route change (this is a SPA, so the pixel's own navigation detection
  // never sees the subsequent "pages"). Both no-op until a pixel id is set and
  // the visitor has accepted cookies.
  React.useEffect(() => {
    captureAttribution();
  }, []);

  React.useEffect(() => {
    initMetaPixel().then(() => trackPixel('PageView'));
    // GA needs the same treatment, and never had it: gtag's automatic pageview
    // fires once on script load and never again, so every in-app navigation
    // after the landing page was missing from GA entirely. Product pages had
    // effectively no traffic data at all.
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // GA only loads once cookie consent is accepted, which is usually after the
  // first route has already rendered — send that view when it comes up.
  React.useEffect(() => {
    const onGaReady = () => trackPageView();
    window.addEventListener('ga:ready', onGaReady);
    return () => window.removeEventListener('ga:ready', onGaReady);
  }, []);
  // Welcome trial modal disabled until June — using Featured Reader promos instead
  // const { showWelcome, triggerWelcome, closeWelcome } = useWelcomeModal();
  // React.useEffect(() => {
  //   if (isAuthenticated && !subLoading && !isSubscriber && location.pathname !== '/trial' && !location.pathname.startsWith('/subscribe/')) {
  //     triggerWelcome();
  //   }
  // }, [isAuthenticated, subLoading, isSubscriber, location.pathname]);

  return (
    <>
      <ScrollToTop />
      {!isStandalone && <AppHeader />}
      {/* <WelcomeModal isOpen={showWelcome} onClose={closeWelcome} /> */}
      {/* The admin-managed promo calendar (/admin, Pop-ups). Dated cards; nothing to revert. */}
      {!isStandalone && <PromoPopup />}
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/ticket-rewards" element={<TicketRewardsLanding />} />
        <Route path="/video-rotator" element={<VideoRotator />} />
        <Route path="/video-compressor" element={<VideoCompressor />} />
        <Route path="/video-stitcher" element={<VideoStitcher />} />
        <Route path="/video-splitter" element={<VideoSplitter />} />
        <Route path="/video-concatenate" element={<VideoConcatenate />} />
        <Route path="/video-transcriber" element={<VideoTranscriber />} />
        <Route path="/video-enhancer" element={<RequireAuth><VideoEnhancerPage /></RequireAuth>} />
        <Route path="/video-enhancer-classic" element={<RequireAuth><VideoEnhancerNLE /></RequireAuth>} />
        <Route path="/audio-enhancer" element={<RequireAuth><AudioEnhancer /></RequireAuth>} />
        <Route path="/background-remover" element={<RequireAuth><BackgroundRemover /></RequireAuth>} />
        <Route path="/scene-edit" element={<RequireAuth><SceneEdit /></RequireAuth>} />
        <Route path="/name-slate" element={<RequireAuth><NameSlate /></RequireAuth>} />
        <Route path="/captions" element={<RequireAuth><Captions /></RequireAuth>} />
        <Route path="/admin" element={<RequireAuth><AdminDashboard /></RequireAuth>} />
        <Route path="/scheduled-posts" element={<RequireAuth><ScheduledPosts /></RequireAuth>} />
        <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
        {/* Stripe portal sessions expire fast, so the linkable thing is this route,
            which mints a fresh one on load. Safe to put in emails and support replies. */}
        <Route path="/billing" element={<RequireAuth><BillingPage /></RequireAuth>} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/audition" element={<RequireAuth><AuditionEnhancer /></RequireAuth>} />
        <Route path="/readers" element={<RequireAuth><ReadersPage /></RequireAuth>} />
        <Route path="/readers/requests" element={<RequireAuth><ReaderSessionsPage /></RequireAuth>} />
        <Route path="/auth" element={<AuthPage />} />
        {/* /login never existed as a route, but the charter page pointed at it on
            launch day, so it is in browser history and in people's tabs. Redirect
            rather than 404 them a second time. */}
        <Route path="/login" element={<Navigate to={`/auth${window.location.search}`} replace />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/trial" element={<TrialPage />} />
        <Route path="/subscribe/reader" element={<SubscribeReaderPage />} />
        <Route path="/readers/profile" element={<RequireAuth><ReaderProfileEditPage /></RequireAuth>} />
        <Route path="/charter" element={<CharterPage />} />
        <Route path="/offer" element={<OfferPage />} />
        <Route path="/drop/:token" element={<DropPage />} />
        {/* Partner co-branded offer pages. One explicit route per partner so it can never shadow a real page. */}
        <Route path="/limelite" element={<PartnerOfferPage partner="limelite" />} />
        {/* Partner sign-in: a partner's Hey Joe button lands here with a single-use ticket. */}
        <Route path="/handoff" element={<PartnerHandoffPage />} />
        <Route path="/reader/payouts" element={<RequireAuth><ReaderPayoutsPage /></RequireAuth>} />
        {/* Stripe onboarding returns here (return_url on /api/affiliate/connect/onboard). */}
        <Route path="/affiliate" element={<RequireAuth><AffiliatePage /></RequireAuth>} />
        <Route path="/reader/:readerId" element={<ReaderProfilePage />} />
        <Route path="/creators/:slug" element={<CreatorStorePage />} />
        <Route path="/course/:productId" element={<CoursePage />} />
        {NAMED_LANDINGS.map(p => <Route key={p} path={p} element={<CoursePage />} />)}
        <Route path="/interview/:token" element={<InterviewGuestPage />} />
        <Route path="/my-store" element={<RequireAuth><MyStorePage /></RequireAuth>} />
        <Route path="/my-store/new" element={<RequireAuth><ListingChooserPage /></RequireAuth>} />
        <Route path="/my-store/new/product" element={<RequireAuth><GoodsBuilderPage /></RequireAuth>} />
        <Route path="/my-store/product/:productId" element={<RequireAuth><GoodsBuilderPage /></RequireAuth>} />
        <Route path="/my-store/bundle/new" element={<RequireAuth><BundleBuilderPage /></RequireAuth>} />
        <Route path="/my-store/bundle/:productId" element={<RequireAuth><BundleBuilderPage /></RequireAuth>} />
        <Route path="/my-store/new/webinar" element={<RequireAuth><WebinarBuilderPage /></RequireAuth>} />
        <Route path="/my-store/webinar/:productId" element={<RequireAuth><WebinarBuilderPage /></RequireAuth>} />
        <Route path="/my-store/new/video" element={<RequireAuth><VideoBuilderPage /></RequireAuth>} />
        <Route path="/my-store/video/:productId" element={<RequireAuth><VideoBuilderPage /></RequireAuth>} />
        <Route path="/my-store/new/course" element={<Navigate to="/my-store/course/new" replace />} />
        <Route path="/my-store/studio" element={<RequireAuth><ImageStudioPage /></RequireAuth>} />
        <Route path="/my-store/interviews/:id" element={<RequireAuth><InterviewHostPage /></RequireAuth>} />
        {/* The talent hosting their own reader session. Same page, different door:
            /my-store is creator territory and a talent who booked a reader is not a
            creator, so sending them there reads as somebody else's dashboard. */}
        <Route path="/session/:id" element={<RequireAuth><InterviewHostPage /></RequireAuth>} />
        <Route path="/my-library" element={<RequireAuth><MyLibraryPage /></RequireAuth>} />
        <Route path="/my-store/course/new" element={<RequireAuth><CourseBuilderPage /></RequireAuth>} />
        <Route path="/my-store/course/:productId" element={<RequireAuth><CourseBuilderPage /></RequireAuth>} />
        <Route path="/my-store/series-studio" element={<RequireAuth><SeriesStudioPage /></RequireAuth>} />
        <Route path="/my-store/series-studio/:projectId" element={<RequireAuth><SeriesStudioPage /></RequireAuth>} />
        <Route path="/my-purchases" element={<RequireAuth><MyPurchasesPage /></RequireAuth>} />
        <Route path="/my-site" element={<RequireAuth><MySitePage /></RequireAuth>} />
        <Route path="/admin/site-review" element={<RequireAuth><SiteReviewPage /></RequireAuth>} />
        <Route path="/admin/sites" element={<RequireAuth><SitesAdminPage /></RequireAuth>} />
        <Route path="/move-your-site" element={<MoveYourSitePage />} />
        <Route path="/scan" element={<ScanPage />} />
        <Route path="/my-subscriptions" element={<RequireAuth><MySubscriptionsPage /></RequireAuth>} />
        <Route path="/purchases/:productId" element={<RequireAuth><ProductViewerPage /></RequireAuth>} />
        <Route path="/marketplace" element={<MarketplacePage />} />
        <Route path="/discover" element={<DiscoverPage />} />
        <Route path="/calendar" element={<WebinarCalendarPage />} />
        {/* Houston Media Conference booth QR code. /hmc is the printed one — short
            enough to say across a trade show table; /houston still works. */}
        <Route path="/hmc" element={<HoustonPage />} />
        <Route path="/hmc/producers" element={<HoustonProducersPage />} />
        <Route path="/houston" element={<HoustonPage />} />
        <Route path="/houston/producers" element={<HoustonProducersPage />} />
        {/* CESD Talent Agency client flyer (Oct 30 2026): 30 days of Plus, card required.
            The trial is the `cesd` row in campaign_promo_codes. */}
        <Route path="/cesd" element={<Navigate to="/plans?promo=cesd" replace />} />
        <Route path="/webinars" element={<Navigate to="/calendar" replace />} />        <Route path="/find-talent" element={<RequireAuth><FindTalentPage /></RequireAuth>} />
        <Route path="/creator-guide" element={<CreatorGuidePage />} />
        {/* Game Show — unlinked (direct-URL only for now) */}
        <Route path="/my-shows" element={<RequireAuth><GameShowsPage /></RequireAuth>} />
        <Route path="/show-host/:showId" element={<RequireAuth><GameShowHostPage /></RequireAuth>} />
        <Route path="/show/:showId" element={<RequireAuth><GameShowAudiencePage /></RequireAuth>} />
        {/* Video Battle: performer self-tape submission (free). */}
        <Route path="/show-submit/:showId" element={<RequireAuth><GameShowSubmitPage /></RequireAuth>} />
        {/* Come On Down: the on-stage contestant desktop player. */}
        <Route path="/show-stage/:showId" element={<RequireAuth><GameShowContestantPage /></RequireAuth>} />
        {/* Embed variant for casting's Game attendee page iframe — NO RequireAuth
            (auth arrives from the parent via postMessage; the API 401s without it). */}
        <Route path="/show-embed/:showId" element={<GameShowAudiencePage />} />
        {/* Sponsor pay page — PUBLIC (sponsors have no account; funds prizes). */}
        <Route path="/sponsor/:token" element={<SponsorPayPage />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isStandalone && <ChatFooter />}
    </>
  );
}

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} disableTransitionOnChange>
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <CookieConsent />
          <BrowserRouter>
            <AuthProvider>
              <AppLayout />
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  </ThemeProvider>
);

export default App;
